import type * as React from "react";
import { useLocalStorage } from "usehooks-ts";

import { cn } from "../utils";
import { Badge } from "./badge";
import {
  Collapsible,
  CollapsibleContent,
  type CollapsibleContentProps,
  type CollapsibleProps,
  CollapsibleTrigger,
  type CollapsibleTriggerProps,
} from "./collapsible";
import { Gutter } from "./column";
import { Icon } from "./icon";

export const ColumnList = ({
  className,
  defaultOpen = true,
  label,
  onOpenChange,
  ...props
}: CollapsibleProps & { label: string }) => {
  const [open, setOpen] = useLocalStorage(`column${label.replaceAll(" ", "")}`, defaultOpen);

  return (
    <Collapsible
      defaultOpen={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        onOpenChange?.(nextOpen);
      }}
      className={cn("group border-t border-border/35 first:border-t-0", className)}
      {...props}
    />
  );
};

export const ColumnListTrigger = ({
  className,
  label,
  count,
  ...props
}: CollapsibleTriggerProps & { label: string; count?: number }) => (
  <CollapsibleTrigger
    className={cn(
      "group bg-background cursor-pointer flex font-semibold h-10 items-center pr-3 text-left text-muted-foreground text-[11px] top-0 w-full z-1 group-data-[state=open]:sticky",
      className
    )}
    {...props}
  >
    <Gutter>
      {count === undefined ? null : (
        <Badge variant="secondary" size="2xs" type="square" className="gap-1 text-muted-foreground">
          {count}
        </Badge>
      )}
    </Gutter>
    <span className="truncate">{label}</span>
    <Icon
      icon="keyboard_arrow_right"
      className="group-disabled:hidden ml-auto size-6 text-base transition-transform group-data-[state=open]:rotate-90"
    />
  </CollapsibleTrigger>
);

export const ColumnListPinned = ({ className, ...props }: React.ComponentProps<"div">) => (
  <div className={cn("px-1 pb-1 space-y-1", className)} {...props} />
);

export const ColumnListContent = ({ className, ...props }: CollapsibleContentProps) => (
  <CollapsibleContent className={cn("group/content px-1 space-y-1 data-[state=open]:pb-1", className)} {...props} />
);
